import React from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { californiaCities, lifeInsuranceKeywords } from '../data/californiaCities';
import { useTranslation } from '../hooks/useTranslation';

const { FiMapPin, FiShield, FiCheckCircle, FiArrowRight, FiPhone, FiMail } = FiIcons;

function CityInsurancePage() {
  const { citySlug, keywordSlug } = useParams();
  const { t } = useTranslation();

  // Find the city and keyword from the slugs
  const city = californiaCities.find(c => 
    c.name.toLowerCase().replace(/\s+/g, '-') === citySlug
  );
  const keyword = lifeInsuranceKeywords.find(k => 
    k.toLowerCase().replace(/\s+/g, '-') === keywordSlug
  );

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const staggerChildren = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.2 } }
  };

  const handleGetQuote = () => {
    window.open('https://quote.burbankinsuranceservices.com/1b0DiB', '_blank');
  };

  if (!city || !keyword) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">{t('cityInsurance.pageNotFound')}</h1>
          <p className="text-gray-600">{t('cityInsurance.requestedNotFound')}</p>
        </div>
      </div>
    );
  }

  const localBenefits = t('cityInsurance.localBenefits').map((benefit, index) => {
    if (index === 1) return `${t('cityInsurance.localBenefits.1')} ${city.name} ${t('cityInsurance.and')} ${city.county}`;
    if (index === 2) return `${t('cityInsurance.localBenefits.2')} ${city.name} ${t('cityInsurance.residents')}`;
    return benefit;
  });

  const localTestimonials = [
    {
      name: `Sarah M.`,
      location: `${city.name}, CA`,
      quote: `Getting life insurance online was so much easier than I expected. As a busy resident of ${city.name}, I appreciated being able to complete everything from home.`,
      coverage: '$500,000',
      premium: '$35/month'
    },
    {
      name: `Michael R.`,
      location: `${city.county}`,
      quote: `The process was transparent and fast. I got approved for coverage in ${city.name} within minutes of applying online.`,
      coverage: '$750,000',
      premium: '$48/month'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 via-white to-indigo-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerChildren}
            className="text-center mb-16"
          >
            <motion.div
              variants={fadeInUp}
              className="flex items-center justify-center mb-6"
            >
              <SafeIcon icon={FiMapPin} className="text-blue-600 text-2xl mr-3" />
              <span className="text-lg text-gray-600">{city.county}, California</span>
            </motion.div>
            
            <motion.h1
              variants={fadeInUp}
              className="text-5xl md:text-6xl font-bold text-gray-900 mb-6"
            >
              {keyword} in <span className="text-blue-600">{city.name}</span>
            </motion.h1>
            
            <motion.p
              variants={fadeInUp}
              className="text-xl text-gray-600 max-w-3xl mx-auto mb-8"
            >
              Get affordable {keyword.toLowerCase()} in {city.name}, California. Licensed CA insurance professionals serving {city.county} and surrounding areas. No medical exam required for most applicants.
            </motion.p>
            
            <motion.button
              variants={fadeInUp}
              onClick={handleGetQuote}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors duration-200 flex items-center justify-center space-x-2 mx-auto"
            >
              <span>{t('cityInsurance.getYourQuote')} {city.name} {t('cityInsurance.quote')}</span>
              <SafeIcon icon={FiArrowRight} />
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Local Benefits */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              {t('cityInsurance.whyChooseUs')} {keyword} in {city.name}?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('cityInsurance.uniqueNeeds')} {city.name} {t('cityInsurance.residents')} {city.county} {t('cityInsurance.families')}.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerChildren}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {localBenefits.map((benefit, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="flex items-start space-x-3"
              >
                <SafeIcon icon={FiCheckCircle} className="text-green-500 text-xl mt-1 flex-shrink-0" />
                <span className="text-gray-700">{benefit}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Local Information */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                {keyword} {t('cityInsurance.coverageFor')} {city.name} {t('cityInsurance.residents')}
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                {t('cityInsurance.livingIn')} {city.name}, {city.county}, {t('cityInsurance.youNeed')} {keyword.toLowerCase()} {t('cityInsurance.policiesDesigned')}
              </p>
              <p className="text-lg text-gray-600 mb-8">
                {t('cityInsurance.longtime')} {city.name} {t('cityInsurance.resident')} {city.county}, {t('cityInsurance.protecting')}
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center space-x-4">
                  <SafeIcon icon={FiShield} className="text-blue-600 text-2xl" />
                  <span className="text-gray-700 font-medium">
                    {t('header.licensedInCalifornia')} ({t('header.licenseNumber')})
                  </span>
                </div>
                <div className="flex items-center space-x-4">
                  <SafeIcon icon={FiMapPin} className="text-blue-600 text-2xl" />
                  <span className="text-gray-700 font-medium">
                    {t('cityInsurance.proudlyServing')} {city.name} {t('cityInsurance.andAllOf')} {city.county}
                  </span>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              className="bg-white rounded-2xl p-8 shadow-lg"
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                {t('cityInsurance.getYourQuote')} {city.name} {t('cityInsurance.quoteTodayBtn')}
              </h3>
              <div className="space-y-4 mb-8">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">{t('cityInsurance.applicationTime')}</span>
                  <span className="font-semibold text-green-600">{t('cityInsurance.minutes')}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">{t('cityInsurance.medicalExam')}</span>
                  <span className="font-semibold text-green-600">{t('cityInsurance.usuallyNotRequired')}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">{t('cityInsurance.approvalTime')}</span>
                  <span className="font-semibold text-green-600">{t('cityInsurance.oftenInstant')}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">{t('cityInsurance.coverageArea')}</span>
                  <span className="font-semibold text-blue-600">{city.name}, CA</span>
                </div>
              </div>
              
              <button
                onClick={handleGetQuote}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-4 rounded-lg font-semibold transition-colors duration-200 mb-4"
              >
                {t('cityInsurance.getQuoteBtn')} {keyword} {t('cityInsurance.quote')}
              </button>
              <p className="text-xs text-gray-500 text-center">
                {t('cityInsurance.licensedProfessionals')}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Local Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-4xl font-bold text-gray-900 text-center mb-16"
          >
            {t('cityInsurance.whatResidentsSay')} {city.name} {t('cityInsurance.residentsSay')}
          </motion.h2>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerChildren}
            className="grid lg:grid-cols-2 gap-8"
          >
            {localTestimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 shadow-lg"
              >
                <blockquote className="text-gray-700 mb-6 italic">
                  "{testimonial.quote}"
                </blockquote>
                <div className="border-t pt-4">
                  <div className="font-semibold text-gray-900">{testimonial.name}</div>
                  <div className="text-sm text-gray-600">{testimonial.location}</div>
                  <div className="mt-2 flex justify-between text-sm">
                    <span className="text-blue-600 font-medium">
                      {t('cityInsurance.coverage')}: {testimonial.coverage}
                    </span>
                    <span className="text-green-600 font-medium">
                      {t('cityInsurance.premium')}: {testimonial.premium}
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerChildren}
          >
            <motion.h2
              variants={fadeInUp}
              className="text-4xl font-bold text-white mb-6"
            >
              {t('cityInsurance.readyToProtect')} {city.name} {t('cityInsurance.family')}
            </motion.h2>
            
            <motion.p
              variants={fadeInUp}
              className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto"
            >
              {t('cityInsurance.dontWait')} {keyword.toLowerCase()} {t('cityInsurance.forCity')} {city.name}, California {t('cityInsurance.today')}.
            </motion.p>
            
            <motion.div variants={fadeInUp}>
              <button
                onClick={handleGetQuote}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors duration-200 flex items-center justify-center space-x-2 mx-auto text-lg"
              >
                <span>{t('cityInsurance.getQuoteNow')} {city.name} {t('cityInsurance.quoteNow')}</span>
                <SafeIcon icon={FiArrowRight} />
              </button>
              <p className="text-gray-400 mt-4">
                {t('cityInsurance.licensedServing')} {city.name} {t('cityInsurance.and')} {city.county}
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default CityInsurancePage;